import { clsx } from 'clsx';
import React, { ReactNode, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { useAnchoredMenuPosition } from '../hooks/useAnchoredMenuPosition';
import { useDismissOnOutside } from '../hooks/useDismissOnOutside';
import type { ChartV2SeriesType } from '../types/chart-v2.types';
import styles from './ChartTypeSelector.module.scss';
import { Chip } from './Chip';
import { AreaIcon, BarIcon, CandleIcon, LineIcon } from './Icons';
import { Toggle } from './Toggle';

/** Single row in the per-series chart-type dropdown. */
export interface ChartTypeSelectorSeriesItem {
  /** Stable identifier — must match the series' `id`. */
  id: string;
  /**
   * Short display for the dropdown row (e.g. asset logo + ticker). Rendered
   * with `pointer-events: none` so any click-handlers embedded in the node
   * don't fire — the row's action is the toggle.
   */
  label: ReactNode;
  /** Current chart type for this series (controls the toggle's value). */
  seriesType: ChartV2SeriesType;
  /** Whether the series is currently hidden — an unchecked toggle. */
  hidden?: boolean;
  /**
   * Series has no OHLC data. Only disables the toggle in the candlestick
   * and bar dropdowns; the line dropdown stays usable so the row can
   * still be hidden and shown.
   */
  disabled?: boolean;
}

/**
 * Props for `<ChartTypeSelector>`.
 *
 * Required: `selected`, `onChange`. Renders the line / candle / bar chips
 * in the top bar; pass `seriesItems` to enable the per-series dropdown.
 */
export interface ChartTypeSelectorProps {
  /**
   * Chart types to render as chips, in display order.
   *
   * @default ['line', 'candlestick', 'bar']
   */
  types?: ChartV2SeriesType[];
  /** Currently selected (global) chart type. */
  selected: ChartV2SeriesType;
  /** Disables every chip in the group. */
  disabled?: boolean;
  /** Subset of `types` to render in a disabled state. */
  disabledTypes?: ChartV2SeriesType[];
  /** Called with the picked chart type when the user clicks a chip. */
  onChange: (seriesType: ChartV2SeriesType) => void;
  /**
   * When provided with more than one entry, clicking any chart-type chip
   * opens a dropdown containing a per-series toggle — mirrors the
   * original chart's "chart type per asset" UX. Omit for a simple
   * global-toggle behaviour.
   */
  seriesItems?: ChartTypeSelectorSeriesItem[];
  /**
   * Called when a toggle inside the per-series dropdown flips. The
   * caller decides how to apply it (usually by updating one series'
   * `seriesType` in its state).
   */
  onSeriesTypeChange?: (
    seriesId: string,
    seriesType: ChartV2SeriesType
  ) => void;
  /**
   * Called when switching a row's toggle off should hide the series (or
   * switching it back on should reveal it). Without it, checked rows
   * have nothing to flip to and the toggle reads as dead.
   *
   * A reveal passes `seriesType` when the row also has to move to the open
   * chip's type, so the caller can apply both as one update rather than
   * following up with `onSeriesTypeChange`.
   */
  onSeriesHiddenChange?: (
    seriesId: string,
    hidden: boolean,
    seriesType?: ChartV2SeriesType
  ) => void;
}

const DEFAULT_TYPES: ChartV2SeriesType[] = ['line', 'candlestick', 'bar'];

const OHLC_TYPES: ChartV2SeriesType[] = ['candlestick', 'bar'];

const ICON_FOR_TYPE: Record<ChartV2SeriesType, React.ReactNode> = {
  line: <LineIcon />,
  area: <AreaIcon />,
  candlestick: <CandleIcon />,
  bar: <BarIcon />,
};

const ARIA_LABEL_FOR_TYPE: Record<ChartV2SeriesType, string> = {
  line: 'Line Chart',
  area: 'Area Chart',
  candlestick: 'Candlestick Chart',
  bar: 'Bar Chart',
};

/**
 * Chart-type chip group for the chartv2 top bar: line / candlestick /
 * bar (and optionally area).
 *
 * In single-series mode, clicking a chip applies the type globally via
 * `onChange`. In multi-series mode (when `seriesItems` has more than
 * one entry and `onSeriesTypeChange` is provided), every chip — line
 * included, as in v1 — opens a per-series toggle dropdown so each
 * series can carry a distinct chart type.
 */
export const ChartTypeSelector = ({
  types = DEFAULT_TYPES,
  selected,
  disabled,
  disabledTypes = [],
  onChange,
  seriesItems,
  onSeriesTypeChange,
  onSeriesHiddenChange,
}: ChartTypeSelectorProps) => {
  const [openDropdownType, setOpenDropdownType] =
    useState<ChartV2SeriesType | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const openChipRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const position = useAnchoredMenuPosition({
    open: !!openDropdownType,
    triggerRef: openChipRef,
    menuRef,
  });

  const canUsePerSeriesDropdown =
    !!seriesItems && seriesItems.length > 1 && !!onSeriesTypeChange;

  useDismissOnOutside({
    open: !!openDropdownType,
    refs: [rootRef, menuRef],
    onDismiss: () => setOpenDropdownType(null),
  });

  const activeSeriesTypes = useMemo(() => {
    if (!seriesItems) {
      return null;
    }

    const counts = { line: 0, candlestick: 0, bar: 0, area: 0 };

    for (const item of seriesItems) {
      counts[item.seriesType] += 1;
    }

    return counts;
  }, [seriesItems]);

  const handleChipClick = (type: ChartV2SeriesType) => {
    // Single-series (or caller didn't opt in to per-series mode): apply
    // globally and close any open dropdown.
    if (!canUsePerSeriesDropdown) {
      setOpenDropdownType(null);
      onChange(type);
      return;
    }

    // Toggle the dropdown for this chip.
    setOpenDropdownType(prev => (prev === type ? null : type));
  };

  return (
    <div
      ref={rootRef}
      className={styles.root}
      data-testid="chartv2-chart-type-selector"
    >
      {types.map(type => {
        const isChipActive =
          activeSeriesTypes !== null && seriesItems && seriesItems.length > 0
            ? activeSeriesTypes[type] === seriesItems.length
            : type === selected;
        const isDropdownOpen = openDropdownType === type;

        return (
          <div
            key={type}
            className={styles.chipWrapper}
            ref={isDropdownOpen ? openChipRef : undefined}
          >
            <Chip
              label=""
              leftIcon={ICON_FOR_TYPE[type]}
              isActive={isChipActive || isDropdownOpen}
              disabled={disabled || disabledTypes.includes(type)}
              onClick={() => handleChipClick(type)}
              aria-label={ARIA_LABEL_FOR_TYPE[type]}
              data-testid={`chartv2-chart-type-${type}`}
            />
            {canUsePerSeriesDropdown &&
              isDropdownOpen &&
              createPortal(
                <div
                  ref={menuRef}
                  className={styles.dropdown}
                  style={{ top: position.top, left: position.left }}
                  role="menu"
                  data-testid={`chartv2-chart-type-dropdown-${type}`}
                >
                  {seriesItems?.map(item => {
                    const isChecked = !item.hidden && item.seriesType === type;
                    // A series with no OHLC bars can't be drawn as
                    // candlestick / bar, but its line row has to stay
                    // live — that is where it gets hidden and shown.
                    const rowDisabled =
                      !!item.disabled && OHLC_TYPES.includes(type);

                    return (
                      <div
                        key={item.id}
                        className={clsx(
                          styles.dropdownItem,
                          rowDisabled && styles.dropdownItemDisabled
                        )}
                        data-testid={`chartv2-chart-type-dropdown-item-${type}-${item.id}`}
                      >
                        <div className={styles.dropdownItemLabel}>
                          {item.label}
                        </div>
                        <Toggle
                          checked={isChecked}
                          disabled={rowDisabled}
                          onChange={() => {
                            if (isChecked) {
                              onSeriesHiddenChange?.(item.id, true);
                              return;
                            }

                            // Reveal carries the type so the caller lands
                            // both as one update — a follow-up
                            // `onSeriesTypeChange` would be applied over the
                            // pre-reveal state and leave the series hidden.
                            if (item.hidden) {
                              onSeriesHiddenChange?.(item.id, false, type);
                              return;
                            }

                            onSeriesTypeChange?.(item.id, type);
                          }}
                          data-testid={`chartv2-chart-type-toggle-${type}-${item.id}`}
                        />
                      </div>
                    );
                  })}
                </div>,
                document.body
              )}
          </div>
        );
      })}
    </div>
  );
};
